import { emptySplitApi } from '../../../../../../src/store/emptyApi';
import { PeriodicTaskRead } from '../../../../../../src/store/generated/admin/system-health';

// Define the response types for the unified endpoint
export interface SystemHealthResponse {
  registry_tasks: any[];
  periodic_tasks: PeriodicTaskRead[];
  orphaned_tasks: any[];
}

export interface DiagnosticsResponse {
  task_results: {
    total: number;
    recent_7_days: number;
  };
  celery_config: {
    broker_url: string;
    result_backend: string;
    queue_prefix: string;
    default_queue: string;
  };
  server_name: string;
  periodic_tasks: {
    total: number;
    enabled: number;
  };
  queues: Record<string, { queue: string }>;
}

export interface RunTaskRequest {
  task_name: string;
  arguments: any[];
  kwargs: Record<string, any>;
}

export interface RunTaskResponse {
  success: boolean;
  task_id?: string;
  error?: string;
}

export interface DeleteOldTasksResponse {
  message: string;
}

export interface TaskExecutionMetrics {
  last_execution_status: string | null;
  last_execution_time: string | null;
  success_rate: number | null;
  total_executions: number;
  successful_executions: number;
  failed_executions: number;
}

export interface TaskMetricsResponse {
  task_metrics: Record<string, TaskExecutionMetrics>;
  period_days: number;
  generated_at: string;
}

export interface ClientsAnalyticsRow {
  label: string;
  count: number;
  percentage: number;
}

export interface ClientsAnalyticsResponse {
  filters: {
    device_types: string[];
    operating_systems: string[];
    browsers: string[];
  };
  totals: {
    submissions: number;
    mobile_percentage: number;
    most_common_os: string;
    most_common_browser: string;
  };
  charts: {
    device_types: ClientsAnalyticsRow[];
    operating_systems: ClientsAnalyticsRow[];
    browsers: ClientsAnalyticsRow[];
  };
  meta: {
    suppressed: boolean;
    min_group_size: number;
  };
}

export interface ClientsAnalyticsRequest {
  surveyType?: 'all' | 'pulse' | 'culture';
  deviceType?: string;
  operatingSystem?: string;
  browser?: string;
  organizationId?: number;
  allOrganizations?: boolean;
}

export const taskManagementApi = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    // Main endpoint to get all task data for the page
    getSystemHealthData: build.query<SystemHealthResponse, void>({
      query: () => ({
        url: '/api/admin/v1/system-health/',
        params: { _t: Date.now() }, // Cache busting
      }),
      providesTags: ['TaskManagement'],
    }),

    // Get diagnostics data
    getDiagnostics: build.query<DiagnosticsResponse, void>({
      query: () => '/api/admin/v1/system-health/diagnostics/',
      providesTags: ['TaskManagement'],
    }),

    // Update task (enable/disable)
    updateTask: build.mutation<PeriodicTaskRead, { id: number; enabled: boolean }>({
      query: ({ id, enabled }: { id: number; enabled: boolean }) => ({
        url: `/api/admin/v1/system-health/${id}/`,
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: ['TaskManagement'],
    }),

    // Update task with full data (edit modal)
    updateTaskFull: build.mutation<PeriodicTaskRead, { id: number; data: any }>({
      query: ({ id, data }: { id: number; data: any }) => ({
        url: `/api/admin/v1/system-health/${id}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['TaskManagement'],
    }),

    // Run a periodic task
    runPeriodicTask: build.mutation<RunTaskResponse, RunTaskRequest>({
      query: (body) => ({
        url: '/api/task-management/management/run_task/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TaskManagement'],
    }),

    // Run any task (registry task)
    runAnyTask: build.mutation<RunTaskResponse, RunTaskRequest>({
      query: (body) => ({
        url: '/api/task-management/management/run_task/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TaskManagement'],
    }),

    // Delete orphaned tasks
    deleteOrphanedTasks: build.mutation<DeleteOldTasksResponse, void>({
      query: () => ({
        url: '/api/admin/v1/system-health/delete_old_tasks/',
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['TaskManagement'],
    }),

    // Delete individual task
    deleteTask: build.mutation<{ message: string }, { id: number }>({
      query: ({ id }: { id: number }) => ({
        url: `/api/admin/v1/system-health/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TaskManagement'],
    }),

    // Get task execution metrics
    getTaskExecutionMetrics: build.query<TaskMetricsResponse, void>({
      query: () => '/api/admin/v1/system-health/task_execution_metrics/',
      providesTags: ['TaskManagement'],
    }),

    // Aggregated survey client analytics for pulse + debrief submissions
    getClientsAnalytics: build.query<ClientsAnalyticsResponse, ClientsAnalyticsRequest>({
      query: (params) => ({
        url: '/api/admin/v1/system-health/clients/',
        params: {
          survey_type: params.surveyType,
          device_type: params.deviceType,
          operating_system: params.operatingSystem,
          browser: params.browser,
          organization_id: params.organizationId,
          all_organizations: params.allOrganizations,
        },
      }),
      providesTags: ['TaskManagement'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSystemHealthDataQuery,
  useGetDiagnosticsQuery,
  useUpdateTaskMutation,
  useUpdateTaskFullMutation,
  useRunPeriodicTaskMutation,
  useRunAnyTaskMutation,
  useDeleteOrphanedTasksMutation,
  useDeleteTaskMutation,
  useGetTaskExecutionMetricsQuery,
  useGetClientsAnalyticsQuery,
} = taskManagementApi;